import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Design } from '@noorview/core';
import { ASPECTS } from '@noorview/core';
import { screenStatus, STATUS_LABEL, useApp, useScreenBroadcasts } from '../data/store';
import { StageView } from '../ui/StageView';
import { Icon } from '../ui/icons';
import { liveDesign } from '../app/designs';

/** Figma "Fullscreen · Main Hall": the screen at full size, with a small status pill and exit button. */
export function Fullscreen({ screenId, design, label, onClose }: { screenId: string; design?: Design; label?: string; onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const now = useApp((s) => s.now);
  const screen = ws.screens.find((s) => s.id === screenId);
  const bcs = useScreenBroadcasts(screenId);
  const d = design ?? liveDesign(ws, screenId, now);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    window.addEventListener('keydown', onKey);
    root.current?.focus();
    return () => { window.removeEventListener('keydown', onKey); if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined); };
  }, [onClose]);
  // The Studio's blank canvas previews without a screen.
  if (!screen && !design) return null;
  const name = screen?.name ?? 'Blank canvas';
  const st = screen ? screenStatus(screen, now) : 'unpaired';
  const live = st === 'online' || st === 'idle' || st === 'off';
  const size = d ? ASPECTS[d.aspect] : ASPECTS['16:9'];
  return createPortal(
    <div ref={root} className="fullscreen" role="dialog" aria-modal="true" aria-label={`${name} full screen`} tabIndex={-1}>
      {d ? <StageView design={d} masjid={ws.masjid} timetable={ws.timetable} power={design || !screen ? 'on' : screen.power} salahBlank={design || !screen ? false : screen.salahBlank !== false} mode="screen" screenName={name} broadcasts={bcs} className="stage-fill" label={`${name} full screen`} />
        : <div className="empty" style={{ color: '#fff' }}><b style={{ color: '#fff' }}>Nothing published yet</b></div>}
      <div className="fs-controls">
        <span className="fs-pill"><b>{name}</b><small>{label ?? (live ? 'Live' : STATUS_LABEL[st])} · {size.width} × {size.height}</small></span>
        <button type="button" className="fs-btn" aria-label="Use the whole display" onClick={() => void root.current?.requestFullscreen?.().catch(() => undefined)}><Icon name="expand" size={18} /></button>
        <button type="button" className="fs-btn" aria-label="Exit full screen" onClick={onClose}><Icon name="collapse" size={18} /></button>
      </div>
      <p className="fs-hint">Press Esc or tap <Icon name="collapse" size={12} /> to exit</p>
    </div>, document.body);
}
